import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Input } from "../../ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../ui/select";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../ui/popover";
import { Check, ChevronsUpDown, FileCog } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from "../../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Plus, Settings, Users, FileText, Trash2, Monitor, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "../../ui/command";
import { myLocalStorage } from "../../StorageHelper";
import { copyProfile, createPendingProfile, deleteCopiedProfiles, fetchPendingProfiles, getProfiles, updateGlobalOperatingMode, updatePolicyMode, updatePolicyTime } from "./Api/policyprofileapi";
import { PolicyProfiles } from "./interface";
import { useNavigate } from "react-router-dom";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";


const defaultTimeOptions = [
    { value: 60, label: "60 min" },
    { value: 120, label: "120 min" },
    { value: 180, label: "180 min" },
    { value: 240, label: "240 min" }
];

export default function PolicyProfileDetails() {
    const [profiles, setProfiles] = useState<PolicyProfiles[]>([]);
    const [activeTab, setActiveTab] = useState<'windows' | 'linux' | 'mac'>('windows');
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem('latestTenant')?.tenantName || '';
    const tenant = myLocalStorage.getItem("latestTenant");

    // Create profile dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");
    const [selectedOS, setSelectedOS] = useState<'windows' | 'linux' | 'mac'>('windows');
    const [selectedCloneProfile, setSelectedCloneProfile] = useState("");
    const [openCloneSelect, setOpenCloneSelect] = useState(false);


    const navigate = useNavigate();

    const handleOperatingModeChange = async (profileId: number, newMode: string) => {
        setProfiles(prev =>
            prev.map(profile =>
                profile.id === profileId
                    ? { ...profile, mode: newMode as PolicyProfiles['mode'] }
                    : profile
            )
        );

        let response = await updatePolicyMode(profileId, tenantName, newMode)
        if (response) {
            toast({ title: "Operating mode updated successfully" });
        }
    };

    const handleMode = async (newMode: string) => {
        let response = await updateGlobalOperatingMode(tenantName, newMode);
        if (response) {
            toast({ title: "Operating mode updated successfully" });
            const tenant = myLocalStorage.getItem("latestTenant");

            if (tenant) {
                const parsedTenant = typeof tenant === "string" ? JSON.parse(tenant) : tenant;

                const updatedTenant = { ...parsedTenant, mode: newMode };

                myLocalStorage.setItem("latestTenant", updatedTenant);
            }
            fetchProfiles(tenantName)

        }

    }

    const handleDefaultTimeChange = async (profileId: number, newTime: string) => {
        setProfiles((prev = []) =>
            prev.map(profile =>
                profile.id === profileId
                    ? { ...profile, defaultTime: parseInt(newTime) }
                    : profile
            )
        );
        try {
            await updatePolicyTime(profileId, Number(newTime));
            toast({ title: "Default time updated successfully" });
        } catch (e) {

        }

    };

    const handleDelete = async (profileId: number) => {
        const id = profileId;
        const profile = profiles?.find(p => p.id === id);

        if (profile?.isCopied && profile?.canBeDeleted) {
            setProfiles((prev = []) => prev.filter(p => p.id !== id));
            await deleteCopiedProfiles(profileId);
            toast({ title: `Profile "${profile.name}" deleted successfully` });
        } else {
            toast({ title: `Profile "${profile?.name}" is configured in the system and cannot be deleted` });
        }
    };

    const handleAction = (action: string, profileName: string, id: number) => {
        if (action === "Create new profile") {
            setShowCreateDialog(true);
        } else if (action === "Details") {
            localStorage.setItem('currentProfileName', profileName);
            navigate(`/policyProfiles/resources/${id}/${activeTab}`, { state: { profileName } });
        } else if (action === "Categories") {
            const profile = profiles?.find(p => p.name === profileName);
            const osType = profile?.os || "windows";
            localStorage.setItem('currentProfileName', profileName);
            navigate(
                `/policyProfiles/categories/${profile?.id}/${activeTab}/${osType}`,
                { state: { profileName } }
            );
        } else if (action === "Settings") {
            const profile = profiles?.find(p => p.name === profileName);
            const osType = profile?.os || "windows";
            localStorage.setItem('currentProfileName', profileName);
            navigate(
                `/policyProfiles/settings/${profile?.id}/${activeTab}/${osType}`,
                { state: { profileName } }
            );
        } else {
            // No action
        }
    };

    // Get available profiles for cloning based on selected OS
    const getAvailableProfilesForCloning = () => {
        return profiles.filter(profile => profile.os === selectedOS);
    };

    // Handle create profile form submission
    const handleCreateProfile = async () => {
        if (!newProfileName.trim()) {
            toast({ title: "Please enter a profile name", variant: "destructive" });
            return;
        }

        if (!selectedCloneProfile) {
            toast({ title: "Please select a profile to clone from", variant: "destructive" });
            return;
        }

        const cloneFromProfile = profiles.find(p => p.id === Number(selectedCloneProfile));
        if (!cloneFromProfile) return;


        let response = await createPendingProfile(cloneFromProfile.id, newProfileName)

        if (response) {
            await fetchPendingProfiles(tenantName);
            await copyProfile(cloneFromProfile?.id, newProfileName);
        }

        // Reset form and close dialog
        setNewProfileName("");
        setSelectedOS('windows');
        setSelectedCloneProfile("");
        setOpenCloneSelect(false);
        setShowCreateDialog(false);
        fetchProfiles(tenantName)
    };

    // Reset clone profile when OS changes
    useEffect(() => {
        const availableProfiles = getAvailableProfilesForCloning();
        if (availableProfiles.length > 0) {
            // Look for original profile first (e.g., windows_original)
            const originalProfile = availableProfiles.find(profile =>
                profile.name === `${selectedOS}_original`
            );
        }
    }, [selectedOS, profiles]);

    const filteredProfiles = profiles.filter(profile => {
        const matchesOS = profile.os === activeTab;
        const matchesSearch = searchTerm === "" ||
            profile.name.toLowerCase().includes(searchTerm.toLowerCase())
        return matchesOS && matchesSearch;
    });

    const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedProfiles = filteredProfiles.slice(startIndex, startIndex + itemsPerPage);

    const handleTabChange = (value: string) => {
        setActiveTab(value as typeof activeTab);
        setCurrentPage(1);
        setSearchTerm("");
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };



    const fetchProfiles = async (tenantName: string) => {
        let response: PolicyProfiles[] = await getProfiles(tenantName);
        if (response) {
            setProfiles(response);
        }
    }


    useEffect(() => {
        fetchProfiles(tenantName);
    }, [tenantName])


    const getOperatingModeCardStyling = (operatingMode: string, mode: string) => {
        let cardMode = operatingMode;
        if (operatingMode === "INHERITED_FROM_TENANT") cardMode = mode;
        switch (cardMode.toLowerCase()) {
            case "auditing":
                return "border border-gray-200 dark:border-gray-700 border-l-8 border-l-green-500 dark:border-l-green-400";
            case "enforcing":
                return "border border-gray-200 dark:border-gray-700 border-l-8 border-l-orange-500 dark:border-l-orange-400";
            case "disabled":
                return "border border-gray-200 dark:border-gray-700 border-l-8 border-l-gray-400 dark:border-l-gray-500";
            default:
                return "border border-gray-200 dark:border-gray-700 border-l-8 border-l-gray-400 dark:border-l-gray-500";
        }
    };

    const renderProfileCard = (profile: PolicyProfiles) => (
        <Card key={profile.id} className={`hover-elevate cursor-pointer transition-transform hover:scale-[1.01] shadow-sm transition-all duration-200 ${getOperatingModeCardStyling(profile.mode, tenant.mode)}`}>
            <CardContent className="p-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="mb-2">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-semibold text-foreground truncate" data-testid={`text-profile-name-${profile.id}`}>
                                    {profile.name}
                                </h3>
                                {!profile.isCopied ? (
                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded border border-blue-200">
                                        Built-in
                                    </span>
                                ) : (
                                    <span className="px-1.5 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded border border-green-200">
                                        Custom
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Default Time - Left side */}
                        <div className="flex flex-col items-start gap-0.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Default Time</label>
                            <Select
                                value={profile.defaultTime.toString()}
                                onValueChange={(value) => handleDefaultTimeChange(profile.id, value)}
                            >
                                <SelectTrigger
                                    className="w-20 h-8 text-xs border-input"
                                    data-testid={`select-default-time-${profile.id}`}
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {defaultTimeOptions.map(option => (
                                        <SelectItem key={option.value} value={option.value.toString()}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Operating Mode - Middle */}
                    <div className="flex flex-col items-start gap-0.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Operating Mode</label>
                        <Select
                            value={profile.mode}
                            onValueChange={(value) => handleOperatingModeChange(profile.id, value)}
                        >
                            <SelectTrigger
                                className="w-44 h-8 text-xs border-input"
                                data-testid={`select-operating-mode-${profile.id}`}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AUDITING">AUDITING</SelectItem>
                                <SelectItem value="ENFORCING">ENFORCING</SelectItem>
                                <SelectItem value="DISABLED">DISABLED</SelectItem>
                                <SelectItem value="INHERITED_FROM_TENANT">INHERITED {`(${tenant.mode})`}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Action Buttons - Right side */}
                    <div className="flex flex-col items-start gap-0.5">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</label>
                        <div className="flex items-center gap-1.5">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAction("Details", profile.name, profile.id)}
                                className="h-8 px-2 text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900"
                                data-testid={`button-details-${profile.id}`}
                            >
                                <FileText className="w-3 h-3 mr-1" />
                                Resources
                            </Button>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAction("Categories", profile.name, profile.id)}
                                className="h-8 px-2 text-xs bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900"
                                data-testid={`button-categories-${profile.id}`}
                            >
                                <Users className="w-3 h-3 mr-1" />
                                Categories
                            </Button>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    myLocalStorage.setItem("profilesDetails", profile)
                                    handleAction("Settings", profile.name, profile.id)
                                }}
                                className="h-8 px-2 text-xs bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/50 dark:text-slate-300 dark:border-slate-700"
                                data-testid={`button-settings-${profile.id}`}
                            >
                                <Settings className="w-3 h-3 mr-1" />
                                Settings
                            </Button>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(profile.id)}
                                disabled={!profile.isCopied}
                                className={`h-8 px-2 text-xs ${profile.isCopied
                                    ? 'text-destructive border-destructive/20 hover:bg-destructive/5'
                                    : 'text-muted-foreground border-muted-foreground/20 cursor-not-allowed'
                                    }`}
                                data-testid={`button-delete-${profile.id}`}
                            >
                                <Trash2 className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="p-6 space-y-6 bg-gradient-to-br from-background to-muted/20 min-h-screen">
            <div className="flex items-center justify-between gap-2">


                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center border border-purple-200 dark:border-purple-700">
                        <FileCog className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                            Policy Profiles
                        </h1>
                        <p className="text-muted-foreground">
                            Manage security profiles and resources categories for each platform
                        </p>
                    </div>
                </div>




                <div className="flex items-center gap-3">


                    <Select
                        value={tenant.mode ? tenant.mode : " "}
                        onValueChange={(value: string) => handleMode(value)}
                    >
                        <SelectTrigger
                            className="w-40 h-8 text-xs border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            data-testid="select-status"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem
                                value="AUDITING"
                                data-testid="option-status-allowed"
                            >
                                AUDITING
                            </SelectItem>
                            <SelectItem
                                value="ENFORCING"
                                data-testid="option-status-denied"
                            >
                                ENFORCING
                            </SelectItem>
                            <SelectItem
                                value="DISABLED"
                                data-testid="option-status-timed"
                            >
                                DISABLED
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <Button
                        onClick={() => handleAction("Create new profile", "new profile", -1)}
                        data-testid="button-create-profile"
                        size="sm"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create new profile
                    </Button>
                </div>



            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <div className="flex justify-center">
                    <TabsList className="grid grid-cols-3 bg-muted/50 p-1 h-11 w-auto">
                        <TabsTrigger
                            value="windows"
                            className="gap-2 font-medium data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
                            data-testid="tab-windows"
                        >
                            <FaWindows className=" data-[state=active]:text-white" />
                            WINDOWS
                        </TabsTrigger>
                        <TabsTrigger
                            value="linux"
                            className="gap-2 font-medium data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
                            data-testid="tab-linux"
                        >
                            <FaLinux className="w-6 h-6 text-black dark:text-white" />
                            LINUX
                        </TabsTrigger>
                        <TabsTrigger
                            value="mac"
                            className="gap-2 font-medium data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm"
                            data-testid="tab-mac"
                        >
                            <FaApple className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                            MAC
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* Search Bar */}
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                        placeholder="Search profiles..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pl-10"
                        data-testid="input-search-profiles"
                    />
                </div>

                <TabsContent value="windows" className="space-y-4 mt-6">
                    {paginatedProfiles.length > 0 ? (
                        paginatedProfiles.map(renderProfileCard)
                    ) : (
                        <Card className="border-border/40 ">
                            <CardContent className="p-8 text-center">
                                <Monitor className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-muted-foreground" data-testid="text-no-profiles">
                                    {searchTerm ? `No Windows profiles found matching "${searchTerm}"` : 'No Windows profiles found'}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="linux" className="space-y-4 mt-6">
                    {paginatedProfiles.length > 0 ? (
                        paginatedProfiles.map(renderProfileCard)
                    ) : (
                        <Card className="border-border/40">
                            <CardContent className="p-8 text-center">
                                <Monitor className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-muted-foreground" data-testid="text-no-profiles">
                                    {searchTerm ? `No Linux profiles found matching "${searchTerm}"` : 'No Linux profiles found'}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="mac" className="space-y-4 mt-6">
                    {paginatedProfiles.length > 0 ? (
                        paginatedProfiles.map(renderProfileCard)
                    ) : (
                        <Card className="border-border/40">
                            <CardContent className="p-8 text-center">
                                <Monitor className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-muted-foreground" data-testid="text-no-profiles">
                                    {searchTerm ? `No macOS profiles found matching "${searchTerm}"` : 'No macOS profiles found'}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Pagination */}
                {filteredProfiles.length > itemsPerPage && (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm text-muted-foreground">
                            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredProfiles.length)} of {filteredProfiles.length} profiles
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                data-testid="button-prev-page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </Button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setCurrentPage(page)}
                                        className="w-8 h-8 p-0"
                                        data-testid={`button-page-${page}`}
                                    >
                                        {page}
                                    </Button>
                                ))}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                data-testid="button-next-page"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </Tabs>

            {/* Create Profile Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Profile</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Profile Name Input */}
                        <div className="space-y-2">
                            <Label htmlFor="profileName">Profile Name</Label>
                            <Input
                                id="profileName"
                                placeholder="Enter profile name"
                                value={newProfileName}
                                onChange={(e) => setNewProfileName(e.target.value)}
                                data-testid="input-profile-name"
                            />
                        </div>

                        {/* OS Selection */}
                        <div className="space-y-2">
                            <Label>Operating System</Label>
                            <RadioGroup
                                value={selectedOS}
                                onValueChange={(value) => setSelectedOS(value as typeof selectedOS)}
                                className="flex gap-6"
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="windows" id="windows" data-testid="radio-windows" />
                                    <Label htmlFor="windows">Windows</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="linux" id="linux" data-testid="radio-linux" />
                                    <Label htmlFor="linux">Linux</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="mac" id="mac" data-testid="radio-mac" />
                                    <Label htmlFor="mac">macOS</Label>
                                </div>
                            </RadioGroup>
                        </div>

                        {/* Clone From Profile */}
                        <div className="space-y-2">
                            <Label>Clone from Profile</Label>
                            <Popover open={openCloneSelect} onOpenChange={setOpenCloneSelect}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCloneSelect}
                                        className="w-full justify-between"
                                        data-testid="select-clone-profile"
                                    >
                                        {selectedCloneProfile
                                            ? getAvailableProfilesForCloning().find((profile) => profile.id === Number(selectedCloneProfile))?.name
                                            : "Select profile to clone..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0">
                                    <Command>
                                        <CommandInput placeholder="Search profiles..." />
                                        <CommandEmpty>No profiles found.</CommandEmpty>
                                        <CommandGroup>
                                            {getAvailableProfilesForCloning().map((profile) => (
                                                <CommandItem
                                                    key={profile.id}
                                                    value={profile.name}
                                                    onSelect={() => {
                                                        setSelectedCloneProfile(String(profile.id));
                                                        setOpenCloneSelect(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={`mr-2 h-4 w-4 ${selectedCloneProfile === String(profile.id) ? "opacity-100" : "opacity-0"
                                                            }`}
                                                    />
                                                    {profile.name} ({profile.isCopied ? 'Custom' : 'Built-in'})
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowCreateDialog(false)}
                                data-testid="button-cancel-create"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateProfile}
                                disabled={!newProfileName.trim()}
                                data-testid="button-confirm-create"
                            >
                                Create Profile
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}